import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, TextField, MenuItem, Card, CircularProgress } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, PlayArrow } from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import StudentLayout from '../StudentLayout';
import QuestionList from '../../../components/Dashboard/QuestionList';
import { useTranslation } from 'react-i18next';

function BookmarksPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedCourse } = useContext(AuthContext);
  const { t } = useTranslation();
  const notebookIdFromUrl = searchParams.get('notebook');

  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('questionNumber');

  useEffect(() => {
    if (selectedCourse && selectedCourse.id) {
      fetchBookmarks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      
      if (!selectedCourse || !selectedCourse.id) {
        console.error('No course selected');
        setBookmarks([]);
        return;
      }
      
      const courseTemplateId = parseInt(selectedCourse.id);
      
      // Fetch all bookmarks and all questions for the current course
      const [bookmarksResponse, questionsResponse] = await Promise.all([
        axios.get('/bookmarks'),
        axios.get('/questions', {
          params: { courseTemplateId }
        })
      ]);
      
      const bookmarkList = bookmarksResponse.data || [];
      const courseQuestions = questionsResponse.data || [];
      
      // Filter bookmarks to only include those in the current course
      // bookmarkList already contains all needed info from Bookmark.getBookmarksByUser
      // For notebook examples, the id is already in format: 'notebook_${exampleId}'
      const filteredBookmarks = bookmarkList.filter(bookmark => {
        if (bookmark.isNotebookExample) {
          // For notebook examples, bookmark.id is already in format 'notebook_${exampleId}' from Bookmark.getBookmarksByUser
          // bookmark.exampleId contains the numeric ID (e.g., 11)
          // bookmark.questionId also contains the numeric ID (e.g., 11)
          const bookmarkId = bookmark.id || `notebook_${bookmark.exampleId || bookmark.questionId}`;
          const numericId = bookmark.exampleId || bookmark.questionId;
          
          // Check if it exists in current course's questions
          return courseQuestions.some(q => {
            if (q.isNotebookExample) {
              const qId = q.id?.toString() || q._id?.toString();
              // Match by full ID (notebook_11) or by numeric ID if we can extract it
              if (qId === bookmarkId) return true;
              if (qId?.startsWith('notebook_')) {
                const qNumericId = qId.replace('notebook_', '');
                return qNumericId === numericId?.toString();
              }
              return false;
            }
            return false;
          });
        } else {
          // For regular questions, check if questionId exists in course questions
          return courseQuestions.some(q => {
            if (!q.isNotebookExample) {
              return q.id?.toString() === bookmark.questionId?.toString() || 
                     q._id?.toString() === bookmark.questionId?.toString();
            }
            return false;
          });
        }
      });
      
      // bookmarks are already in correct format from Bookmark.getBookmarksByUser
      // Just ensure all required fields are present
      const bookmarksForDisplay = filteredBookmarks.map(bookmark => {
        if (bookmark.isNotebookExample) {
          return {
            ...bookmark,
            // Ensure id is in correct format; preserve notebookId for notebook filter
            id: bookmark.id || `notebook_${bookmark.exampleId || bookmark.questionId}`,
            _id: bookmark.id || `notebook_${bookmark.exampleId || bookmark.questionId}`,
            title: bookmark.title || '无标题',
            description: bookmark.title || '',
            category: bookmark.category || '',
            questionType: bookmark.questionType || '',
            sectionTitle: bookmark.sectionTitle || '',
            notebookTitle: bookmark.category || '',
            notebookId: bookmark.notebookId,
            isNotebookExample: true,
            userStatus: bookmark.userStatus || null
          };
        } else {
          return {
            ...bookmark,
            id: bookmark.questionId,
            _id: bookmark.questionId,
            title: bookmark.title || '',
            description: bookmark.description || '',
            category: bookmark.category || '',
            questionNumber: bookmark.questionNumber || '',
            difficulty: bookmark.difficulty || '',
            isNotebookExample: false,
            userStatus: bookmark.userStatus || null
          };
        }
      });
      
      setBookmarks(bookmarksForDisplay);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      setBookmarks([]);
    } finally {
      setLoading(false);
    }
  };

  // 当 URL 带 ?notebook= 时只显示该笔记下的收藏
  const displayedBookmarks = useMemo(() => {
    if (!notebookIdFromUrl) return bookmarks;
    return bookmarks.filter(
      (b) => b.isNotebookExample && b.notebookId === notebookIdFromUrl
    );
  }, [bookmarks, notebookIdFromUrl]);

  const getStatusIcon = (userStatus) => {
    if (!userStatus) return <RadioButtonUnchecked color="disabled" />;
    if (userStatus.status === 'passed') return <CheckCircle color="success" />;
    return <PlayArrow color="primary" />;
  };

  if (!selectedCourse) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography color="text.secondary">请先选择课程</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <StudentLayout appBarTitle={t('dashboard.student.bookmarks')}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              {notebookIdFromUrl ? '本笔记错题本' : t('dashboard.student.bookmarks')}
            </Typography>
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: 150 }}
              label={t('dashboard.student.sortBy')}
            >
              <MenuItem value="questionNumber">{t('dashboard.student.sortByNumber')}</MenuItem>
              <MenuItem value="difficulty">{t('dashboard.student.sortByDifficulty')}</MenuItem>
              <MenuItem value="title">{t('dashboard.student.sortByTitle')}</MenuItem>
              <MenuItem value="status">{t('dashboard.student.sortByStatus')}</MenuItem>
            </TextField>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : displayedBookmarks.length === 0 ? (
            <Card variant="outlined">
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {notebookIdFromUrl
                    ? '本笔记下暂无收藏的题目'
                    : t('dashboard.student.noBookmarks')}
                </Typography>
              </Box>
            </Card>
          ) : (
            <QuestionList
              questions={displayedBookmarks}
              sortBy={sortBy}
              showActions={false}
              getStatusIcon={getStatusIcon}
              onQuestionClick={(question) => {
                if (question.isNotebookExample) {
                  const exampleId = question.id.replace('notebook_', '');
                  navigate(`/notebook-exercise/${exampleId}`);
                } else {
                  navigate(`/question/${question._id || question.id}`);
                }
              }}
            />
          )}
        </Box>
      </Container>
    </StudentLayout>
  );
}

export default BookmarksPage;

